import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createAction, createReducer } from 'redux-act';
import { ThunkDispatch } from 'redux-thunk';
import makeToast, { toast, Toaster } from 'react-hot-toast';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';

export const addToast = createAction('Add toast');
export const removeToast = createAction('Remove toast');
export const toastReducer = createReducer((on: any) => {
  on(addToast, (state: any, { toastId, toast }: any) => ({
    ...state,
    [toastId]: toast,
  }));
  on(removeToast, (state: any, { toastId }: any) => {
    const _state = state;
    // @ts-ignore
    delete _state[toastId];
    return _state;
  });
}, {});

export const pushToasts =
  (toast: Record<string, any>) => (dispatch: ThunkDispatch<any, any, any>) => {
    const toastId = toast.options.toastId || uniqueId('toast-');
    return dispatch(
      // @ts-ignore
      addToast({
        toastId,
        toast,
      }),
    );
  };

export const useToast = () => {
  const dispatch = useDispatch();

  const push = useCallback(
    (content: any, type: any, options: any = undefined) => {
      if (!options) {
        options = {};
      }

      options = type
        ? {
            ...{ type },
            ...options,
          }
        : { ...options };

      return dispatch(
        // @ts-ignore
        pushToasts({
          content,
          options,
        }),
      );
    },
    [dispatch],
  );

  const successToast = (content: any, options = {}) =>
    push(content, 'success', options);
  const errorToast = (content: any, options = {}) =>
    push(content, 'error', options);
  const promiseToast = (content: any, options = {}) =>
    push(content, 'promise', options);
  const customToast = (node: React.ReactNode | Function, options = {}) => {
    push(node, undefined, options);
  };

  const dismissToast = (id: string) => {
    toast.dismiss(id);
  };

  return {
    successToast,
    errorToast,
    promiseToast,
    customToast,
    dismissToast,
  };
};

export const ConnectToastToContainer = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showed, setShowed] = useState<any>([]);
  // @ts-ignore
  const toasts = useSelector(state => state.toasts);

  const defaultToastOptions = {
    duration: 5000,
    draggablePercent: 25,
    style: {
      maxWidth: '450px',
    },
  };

  useEffect(() => {
    const toastsToShow = Object.keys(toasts).filter(function (id) {
      return !(showed || []).includes(id);
    });

    if (toastsToShow.length === 0) {
      return;
    }

    setShowed((prev: any) => [...prev, ...toastsToShow]);

    toastsToShow.forEach(toastId => {
      const toast = toasts[toastId];
      const options = toast.options;
      const combinedOptions = {
        ...defaultToastOptions,
        ...options,
      };

      if (options.type === 'promise') {
        makeToast
          .promise(toast.content, {
            loading: combinedOptions.loading
              ? combinedOptions.loading
              : t('Loading...'),
            success: combinedOptions.success
              ? combinedOptions.success
              : t('Success'),
            error: combinedOptions.error ? combinedOptions.error : t('Error'),
          })
          .then(options.then)
          .catch(options.catch);
        return;
      }

      const isFunc = typeof toast.content === 'function';
      makeToast(isFunc ? toastInfo => toast.content(toastInfo, t) : toast.content, {
        ...combinedOptions,
        // @ts-ignore
        onClose: () => {
          if (typeof options.onClose === 'function') {
            options.onClose();
          }
          // @ts-ignore
          dispatch(removeToast({ toastId }));
        },
      });
    });
  }, [toasts, showed]);

  return (
    <Toaster
      position="bottom-center"
      containerStyle={{
        bottom: 70,
        zIndex: 999999999,
      }}
      toastOptions={{
        className: 'hot_toast',
      }}
    />
  );
};
